export const ROLES = ["duke", "assassin", "captain", "ambassador", "contessa"] as const;
export type Role = (typeof ROLES)[number];

export const ACTION_KINDS = [
  "income",
  "foreignAid",
  "tax",
  "exchange",
  "assassinate",
  "steal",
  "coup",
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export interface Player {
  id: string;
  /** Live cards only; [] means eliminated. */
  hand: Role[];
  coins: number;
}

export interface Action {
  kind: ActionKind;
  actor: string;
  target?: string;
  claim?: Role;
  paid: number;
}

/** Continuations run after a card loss or challenge resolves. */
export type Step = "resolveAction" | "openBlockWindow" | "endTurn" | `loseCard:${string}`;

export type WindowStage = "action" | "blockOnly" | "block";

export type Phase =
  | { t: "awaitAction" }
  | {
      t: "window";
      id: number;
      stage: WindowStage;
      claimant: string;
      claim?: Role;
      eligible: string[];
      allowed: string[];
      block?: { by: string; role: Role };
      deadline: number;
    }
  | { t: "challenge"; accused: string; challenger: string; role: Role; on: "action" | "block" }
  | { t: "loseCard"; playerId: string; then: Step[] }
  | { t: "exchange"; drawn: Role[] }
  | { t: "over"; winner: string };

export interface GameState {
  seed: number;
  rngState: number;
  responseMs: number;
  windowSeq: number;
  players: Player[];
  /** deck[0] is the top card. */
  deck: Role[];
  treasury: number;
  exiled: { role: Role; playerId: string }[];
  turn: { playerIndex: number; round: number; startIndex: number };
  phase: Phase;
  action?: Action;
  log: LogEntry[];
}

export type Response = "allow" | "challenge" | { block: Role };

/** Commands a player sends; the server fills in `by`. */
export type PlayerCommand =
  | { t: "declare"; kind: ActionKind; target?: string }
  | { t: "respond"; windowId: number; response: Response }
  | { t: "reveal" }
  | { t: "concede" }
  | { t: "loseCard"; index: number }
  | { t: "keepCards"; indices: number[] };

/** `kick` and `forfeit` (a player leaving) both remove a player mid-match. */
export type Command =
  | (PlayerCommand & { by: string })
  | { t: "timeout"; windowId: number }
  | { t: "kick"; playerId: string }
  | { t: "forfeit"; by: string };

export interface Ctx {
  now: number;
}

export type Audience = "all" | string;

export type GameEvent = { audience: Audience } & (
  | { t: "coins"; from: string; to: string; amount: number }
  | { t: "claim"; actor: string; kind: ActionKind; target?: string; role?: Role }
  | { t: "bullshit"; by: string; accused: string; role: Role }
  | { t: "block"; by: string; role: Role }
  | { t: "reveal"; playerId: string; role: Role }
  | { t: "drew"; playerId: string; role: Role }
  | { t: "cardLost"; playerId: string; role: Role }
  | { t: "eliminated"; playerId: string }
  | { t: "turn"; playerId: string }
  | { t: "round"; round: number }
  | { t: "win"; winner: string }
);

/** Coin events use this in place of a player id. */
export const TREASURY = "treasury";

export type LogRow =
  | { t: "challenge"; by: string }
  | { t: "block"; by: string; role: Role }
  | { t: "reveal"; by: string; role: Role }
  | { t: "concede"; by: string }
  | { t: "lose"; by: string; role: Role };

export type Outcome =
  | { t: "resolved"; coins?: number }
  | { t: "failed"; refund?: number }
  | { t: "blocked" }
  | { t: "fizzled" }
  | { t: "cancelled" };

export type LogEntry =
  | { t: "round"; round: number }
  | {
      t: "action";
      actor: string;
      kind: ActionKind;
      target?: string;
      claim?: Role;
      paid: number;
      allowed: boolean;
      rows: LogRow[];
      outcome?: Outcome;
    }
  | { t: "eliminated"; playerId: string }
  | { t: "kicked"; playerId: string }
  | { t: "win"; playerId: string };
